<template>
  <div class="team-counter">
    <DowngradeCallout
      v-if="isSubscriptionCancelled && debookingEnabled"
      :title="`Your team is getting downgraded on ${subscriptionRenewalDate}`"
      :subtitle="`Any users marked with this icon are being removed from your account on ${subscriptionRenewalDate}. Any new users you invite will also be removed on that date.`"
      class="mb-16"
    >
      <template #right-item>
        <BittsUserStack size="small" class="ml-auto" :users="users" />
      </template>
    </DowngradeCallout>
    <SupernodeSeatCallout v-if="isSupernodeTier" />
    <BittsLoading :is-loading="!ready">
      <div class="team-counter__seat-cards">
        <TeamCounterCard
          :count="coreSeatCount"
          :limit="coreSeatLimit"
          title="Full Access Seats"
          :requests="fullAccessRequests"
          @open-add-seats-modal="() => (isAddSeatsModalVisible = true)"
        />
        <TeamCounterCard
          :count="salesSeatCount"
          :limit="salesSeatLimit"
          title="Sales Seats"
          :requests="salesAccessRequests"
        />
      </div>
    </BittsLoading>
    <AddSeatsModal
      v-if="isAddSeatsModalVisible"
      @closed="isAddSeatsModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsLoading, BittsUserStack } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import AddSeatsModal from '@/components/billing/AddSeatsModal.vue';
import DowngradeCallout from '@/components/billing/DowngradeCallout.vue';
import SupernodeSeatCallout from '@/components/billing/SupernodeSeatCallout.vue';
import TeamCounterCard from '@/components/team/TeamCounterCard.vue';

import useHasFeature from '@/composables/useHasFeature';
import useSeats from '@/composables/useSeats';
import {
  allReady,
  useBillingStore,
  useSeatRequestsStore,
  useTeamStore,
} from '@/stores';

const { debookingEnabled } = useHasFeature();

const isAddSeatsModalVisible = ref(false);

const billingStore = useBillingStore();
const teamStore = useTeamStore();
const seatRequestsStore = useSeatRequestsStore();
const { isSupernodeTier, isSubscriptionCancelled, subscriptionRenewalDate } =
  storeToRefs(billingStore);
const { fullAccessRequests, salesAccessRequests } =
  storeToRefs(seatRequestsStore);

const ready = allReady(teamStore, billingStore, seatRequestsStore);

const { coreSeatCount, coreSeatLimit, salesSeatCount, salesSeatLimit } =
  useSeats();

const { coreUsers, salesUsers } = storeToRefs(teamStore);
const users = computed(() =>
  [...coreUsers.value, ...salesUsers.value].map((auth) => auth.user),
);

const route = useRoute();
watch(
  () => route.query,
  () => {
    if (route.query.open_add_seats) {
      isAddSeatsModalVisible.value = true;
    }
  },
);
</script>
<style lang="pcss" scoped>
.team-counter {
  @apply flex flex-col gap-16;
}
.team-counter__seat-cards {
  @apply flex justify-between gap-24;
}
</style>
